/**
 * AIW shade quiz (sections/quiz-aiw.liquid), migrated from get.fieracosmetics.com/aiw/lp/quiz (DEV-1368).
 *
 * Every step is server-rendered; this shows one at a time, records the answers, fills the answer-dependent copy,
 * subscribes the email to Klaviyo and hands off to the results page with the match in the URL. The URL is the
 * source of truth, localStorage (`aiw_shadequiz`) is only the fallback for a revisit.
 */

const QUIZ_ID = 'aiw-shadequiz';
const QUIZ_STORAGE_KEY = 'aiw_shadequiz';
const TOTAL_QUESTIONS = 10;
/** A pick waits this long before auto-advancing, so the selection is seen to land. */
const PICK_DELAY = 260;

const UNDERTONES = {
  cool: {
    label: 'Cool / Rosy',
    short: 'Cool',
    points: [
      'The veins on the inside of your wrist are blue/purple.',
      'You rarely tan, but you burn fairly easily.',
      'You look best in white gold, silver and platinum jewelry.',
    ],
  },
  warm: {
    label: 'Warm / Golden',
    short: 'Warm',
    points: [
      'The veins on the inside of your wrist are green/olive.',
      'You tan easily and rarely burn.',
      'You look best in yellow gold, rose gold and copper jewelry.',
    ],
  },
  neutral: {
    label: 'Neutral',
    short: 'Neutral',
    points: [
      'The veins on the inside of your wrist are blue/green.',
      'You sometimes tan, but you sometimes burn, too.',
      'You look great in white gold AND yellow gold jewelry.',
    ],
  },
};

/**
 * The ten shades in the tone grid's order: name, swatch colour, and the line shown under the revealed shade. The
 * colours are a fallback: the section passes the store's own swatches (shade_color metaobject), which replace them.
 */
const SHADES = [
  ['Light', '#F5DBD2', 'Fair'],
  ['Bisque', '#F3CFC0', 'Light'],
  ['Beige', '#ECC9BC', 'Light'],
  ['Sand', '#EBBEA7', 'Light-Medium'],
  ['Medium', '#DDA690', 'Medium'],
  ['Tan', '#C59073', 'Medium-Tan'],
  ['Caramel', '#A67058', 'Deep'],
  ['Dark', '#865B4B', 'Deep'],
  ['Deep', '#734F3E', 'Deep'],
  ['Espresso', '#5A2F19', 'Deepest'],
].map(([name, color, depth]) => ({ name, color, depth }));

const GIFT_WHY = {
  Brush: 'You already love a brush. This one is shaped for concealer, so a tiny dot covers more.',
  Blender: 'Sponges soak up product. This brush uses less and blends just as seamlessly.',
  Fingers: 'Fingertips can drag and streak. This brush presses coverage in for an airbrushed look.',
  'None / Other': 'The easiest way to get a flawless, airbrushed finish, even if you’re new to concealer.',
};

const LOCKED_TYPES = ['loading', 'result', 'disc'];
const NEXT_TYPES = ['multi', 'inter', 'gift'];

function safeStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* storage is blocked in iOS private mode; the URL carries the answers */
  }
}

class AiwQuiz extends HTMLElement {
  constructor() {
    super();
    this.applyStoreSwatches();
    this.steps = Array.from(this.querySelectorAll('.aiw-quiz__step'));
    this.answers = {};
    this.history = [];
    this.index = 0;
    this.timers = [];
    this.pendingPick = null;

    this.backButton = this.querySelector('.aiw-quiz__back');
    this.progress = this.querySelector('.aiw-quiz__progress');
    this.progressFill = this.querySelector('.aiw-quiz__progress-fill');
    this.count = this.querySelector('.aiw-quiz__count');
    this.main = this.querySelector('.aiw-quiz__main');
    this.next = this.querySelector('.aiw-quiz__next');
    this.nextButton = this.querySelector('.aiw-quiz__next-button');
    this.nextNote = this.querySelector('.aiw-quiz__next-note');
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.querySelector('.aiw-quiz__email').addEventListener('submit', this.onEmailSubmit.bind(this));
    this.querySelector('.aiw-quiz__email-input').addEventListener('input', () => this.setEmailError(''));
    this.render();
  }

  /** Uses the product page's shade swatches, so a shade looks the same here, on the results page and on the PDP. */
  applyStoreSwatches() {
    let swatches = {};
    try {
      swatches = JSON.parse(this.querySelector('[data-swatches]')?.textContent || '{}');
    } catch (e) {
      console.error('[aiw-quiz] could not read the shade swatches', e);
    }
    SHADES.forEach((shade) => {
      if (swatches[shade.name]) shade.color = swatches[shade.name];
    });
  }

  get step() {
    return this.steps[this.index];
  }

  stepIndex(id) {
    return this.steps.findIndex((step) => step.dataset.id === id);
  }

  onClick(event) {
    const target = event.target.closest('button');
    if (!target || !this.contains(target)) return;

    if (target === this.backButton) return this.back();
    if (target === this.nextButton) return this.goNext();
    if (target.classList.contains('aiw-quiz__button--yes')) return this.go(this.stepIndex('excited'));
    if (target.classList.contains('aiw-quiz__button--no')) return this.go(this.stepIndex('para'));
    if (target.classList.contains('aiw-quiz__button--reveal')) return this.reveal();
    if (target.dataset.value !== undefined && this.step.contains(target)) this.pick(target.dataset.value);
  }

  pick(value) {
    const { id, type } = this.step.dataset;

    if (type === 'multi') {
      const selected = Array.isArray(this.answers[id]) ? this.answers[id] : [];
      this.answers[id] = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
      this.render();
      return;
    }

    if (type === 'para') {
      this.answers.undertone = value;
      this.answers.paraPicked = true;
    } else {
      this.answers[id] = value;
    }
    this.render();

    clearTimeout(this.pendingPick);
    const from = this.index;
    this.pendingPick = setTimeout(() => this.advance(from), PICK_DELAY);
  }

  advance(from) {
    const id = this.steps[from].dataset.id;
    if (id === 'undertone') {
      return this.go(this.stepIndex(this.answers.undertone === 'unsure' ? 'para' : 'confirm'));
    }
    if (id === 'para') return this.go(this.stepIndex('excited'));
    this.go(from + 1);
  }

  /** Moves to a step, pushing history unless `push` is false (the timed steps and the email gate). */
  go(to, push = true) {
    clearTimeout(this.pendingPick);
    this.clearTimers();
    if (push) this.history.push(this.index);
    this.index = to;
    this.setEmailError('');
    this.render();
    this.startTimedStep();
    this.trackStepView();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  back() {
    if (!this.history.length || LOCKED_TYPES.includes(this.step.dataset.type)) return;
    clearTimeout(this.pendingPick);
    this.index = this.history.pop();
    this.render();
    this.trackStepView();
  }

  goNext() {
    const { id, type } = this.step.dataset;
    if (type === 'multi' && !(Array.isArray(this.answers[id]) && this.answers[id].length)) return;
    this.go(this.index + 1);
  }

  clearTimers() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  later(fn, ms) {
    this.timers.push(setTimeout(fn, ms));
  }

  startTimedStep() {
    const { type } = this.step.dataset;

    if (type === 'loading') {
      const fill = this.step.querySelector('.aiw-quiz__meter-fill');
      const text = this.step.querySelector('.aiw-quiz__loading-text');
      fill.style.width = '0%';
      text.textContent = 'Matching your shade…';
      this.later(() => (fill.style.width = '100%'), 60);
      this.later(() => (text.textContent = 'Checking your undertone…'), 1100);
      this.later(() => (text.textContent = 'Reserving your free Concealer Brush…'), 2200);
      this.later(() => this.go(this.index + 1, false), 3400);
    }

    if (type === 'disc') {
      const rows = Array.from(this.step.querySelectorAll('.aiw-quiz__disc-row'));
      const fill = this.step.querySelector('.aiw-quiz__meter-fill');
      const status = this.step.querySelector('.aiw-quiz__disc-status');
      const setStep = (k) => {
        rows.forEach((row, i) => {
          row.classList.toggle('is-done', i < k);
          row.classList.toggle('is-active', i === k);
        });
        fill.style.width = `${Math.round((k / rows.length) * 100)}%`;
        status.textContent =
          k >= rows.length ? 'All savings applied! Taking you to your match…' : 'Applying your savings…';
      };
      setStep(0);
      [1, 2, 3, 4].forEach((k) => this.later(() => setStep(k), k * 850));
      this.later(
        () => {
          safeStorageSet(QUIZ_STORAGE_KEY, JSON.stringify(Object.fromEntries(this.resultParams())));
          window.location.href = this.resultsHref();
        },
        4 * 850 + 900,
      );
    }
  }

  /** The tone pick (Beige if missing), the undertone, and whether they want under-eye coverage. */
  result() {
    const shade = SHADES.find((s) => s.name === this.answers.tone) || SHADES[2];
    const undertone = UNDERTONES[this.answers.undertone] || UNDERTONES.neutral;
    const concerns = Array.isArray(this.answers.concerns) ? this.answers.concerns : [];
    const underEye = this.answers.coverage === 'Under eye coverage' || concerns.includes('Dark under eyes');
    return { shade, undertone, underEye };
  }

  resultParams() {
    const { shade, underEye } = this.result();
    const undertone = this.answers.undertone;
    return new URLSearchParams({
      shade: shade.name,
      ut: typeof undertone === 'string' && undertone !== 'unsure' ? undertone : 'neutral',
      cov: underEye ? 'undereye' : 'full',
      apply: typeof this.answers.apply === 'string' ? this.answers.apply : '',
      concerns: Array.isArray(this.answers.concerns) ? this.answers.concerns.join('|') : '',
    });
  }

  /** The results page setting may already carry a query string, so the answers are merged into it. */
  resultsHref() {
    const url = new URL(this.dataset.resultsUrl, window.location.origin);
    this.resultParams().forEach((value, key) => url.searchParams.set(key, value));
    return `${url.pathname}${url.search}`;
  }

  reveal() {
    this.go(this.stepIndex('disc'), false);
  }

  setEmailError(message) {
    const error = this.querySelector('.aiw-quiz__email-error');
    error.textContent = message;
    this.querySelector('.aiw-quiz__email-input').classList.toggle('is-invalid', !!message);
  }

  onEmailSubmit(event) {
    event.preventDefault();
    // The form stays in the page once the quiz moves on, so a second submit (autofill, key repeat) must not
    // skip the match screen or send the lead twice.
    if (this.step.dataset.type !== 'email') return;
    const email = this.querySelector('.aiw-quiz__email-input').value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setEmailError('Please enter a valid email address.');
      return;
    }
    this.answers.email = email;

    // Each tracker on its own: a blocked or throwing one must never stop the others or hold up the result.
    const track = (name, fn) => {
      try {
        fn();
      } catch (e) {
        console.error(`[aiw-quiz] ${name} failed`, e);
      }
    };
    track('Klaviyo subscribe', () => this.subscribe(email));
    track('dataLayer quiz_lead', () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'quiz_lead', email, quiz_id: QUIZ_ID });
    });
    track('PostHog lead', () => window.posthog && window.posthog.capture('lead', { lead_type: QUIZ_ID, email }));
    track('TriplePixel Contact', () => window.TriplePixel && window.TriplePixel('Contact', { email }));
    this.go(this.index + 1, false);
  }

  /** Klaviyo profile properties: strings, empties dropped. `resultspageurl` is shared by every AIW quiz template. */
  profileProperties() {
    const str = (k) => (typeof this.answers[k] === 'string' ? this.answers[k] : '');
    const list = (k) => (Array.isArray(this.answers[k]) ? this.answers[k].join(', ') : str(k));
    const properties = {
      $aiw_source: 'lp-shadequiz',
      $aiw_funnel: QUIZ_ID,
      $aiw_shade: this.answers.tone ? this.result().shade.name : '',
      $aiw_gift: 'brush',
      $aiw_skin_type: str('skin'),
      $aiw_concerns: list('concerns'),
      $aiw_coverage: str('coverage'),
      $aiw_undertone: str('undertone'),
      $aiw_application: str('apply'),
      $aiw_formula: str('formula'),
      $aiw_frequency: str('freq'),
      $aiw_age_group: str('age'),
      resultspageurl: new URL(this.resultsHref(), window.location.origin).toString(),
    };
    Object.keys(properties).forEach((k) => !properties[k] && delete properties[k]);
    return properties;
  }

  /** A lead Klaviyo did not take is reported, so a broken list setting or a rejected address is visible. */
  reportKlaviyoFailure(reason, detail = {}) {
    console.error(`[aiw-quiz] Klaviyo subscribe failed: ${reason}`, detail);
    if (window.posthog) window.posthog.capture('aiw_quiz_klaviyo_failed', { reason, ...detail });
  }

  subscribe(email) {
    const { klaviyoCompany, klaviyoList } = this.dataset;
    if (!klaviyoCompany || !klaviyoList) {
      this.reportKlaviyoFailure('missing_settings', { company: !!klaviyoCompany, list: !!klaviyoList });
      return;
    }

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyoCompany}`, {
      method: 'POST',
      headers: { revision: '2025-01-15', 'content-type': 'application/json' },
      // Survives the redirect to the results page if Klaviyo is slow to answer.
      keepalive: true,
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'AIW Shade Quiz (offer)',
            profile: { data: { type: 'profile', attributes: { email, properties: this.profileProperties() } } },
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } },
        },
      }),
    })
      .then(async (response) => {
        if (response.ok) return;
        const body = await response.text().catch(() => '');
        this.reportKlaviyoFailure('http_error', { status: response.status, body: body.slice(0, 300) });
      })
      .catch((e) => this.reportKlaviyoFailure('network_error', { message: e.message }));
  }

  /** Every screen after the first is a step view (the AppLovin quiz_step_view event). */
  trackStepView() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'quiz_step_view',
      quiz_id: QUIZ_ID,
      quiz_step_index: this.index,
      quiz_step_id: this.step.dataset.id,
    });
  }

  render() {
    const step = this.step;
    const { id, type } = step.dataset;
    const n = Number(step.dataset.n);
    const locked = LOCKED_TYPES.includes(type);

    this.steps.forEach((s) => (s.hidden = s !== step));
    this.main.classList.toggle('aiw-quiz__main--wide', type === 'tone');

    const progress = type === 'result' ? 100 : Math.round(((n - 1) / TOTAL_QUESTIONS) * 100);
    this.progressFill.style.width = `${progress}%`;
    this.progress.setAttribute('aria-valuenow', progress);
    this.count.textContent = n > TOTAL_QUESTIONS ? 'Done' : `${Math.min(n, TOTAL_QUESTIONS)} / ${TOTAL_QUESTIONS}`;
    this.backButton.disabled = !this.history.length || locked;

    const selected = this.answers[id];
    step.querySelectorAll('.aiw-quiz__option, .aiw-quiz__tile').forEach((option) => {
      const on = Array.isArray(selected) ? selected.includes(option.dataset.value) : selected === option.dataset.value;
      option.classList.toggle('is-selected', on);
      option.setAttribute('aria-pressed', on);
    });
    step.querySelectorAll('.aiw-quiz__para').forEach((para) => {
      const on = this.answers.paraPicked && this.answers.undertone === para.dataset.value;
      para.classList.toggle('is-selected', !!on);
      para.setAttribute('aria-pressed', !!on);
    });

    const showNext = NEXT_TYPES.includes(type);
    this.next.hidden = !showNext;
    this.nextButton.disabled = type === 'multi' && !(Array.isArray(selected) && selected.length);
    this.nextButton.textContent = type === 'gift' ? 'Yes, Reserve My Free Brush' : 'Next';
    this.nextNote.hidden = type !== 'gift';

    const { shade, undertone } = this.result();

    if (type === 'confirm') {
      step.querySelector('.aiw-quiz__confirm-eyebrow').textContent = `You selected ${undertone.label}`;
      step.querySelector('.aiw-quiz__points').replaceChildren(
        ...undertone.points.map((point) => {
          const li = document.createElement('li');
          li.className = 'aiw-quiz__point';
          li.textContent = point;
          return li;
        }),
      );
    }

    if (type === 'gift') {
      step.querySelector('.aiw-quiz__gift-why').textContent = GIFT_WHY[this.answers.apply] || GIFT_WHY['None / Other'];
    }

    if (type === 'result') {
      const face = step.querySelector('.aiw-quiz__match-face');
      face.src = `${this.dataset.imageBase}tone-${shade.name.toLowerCase()}.webp`;
      face.alt = shade.name;
      step.querySelector('.aiw-quiz__match-name').textContent = shade.name;
      step.querySelector('.aiw-quiz__match-desc').textContent = `${shade.depth} · ${undertone.short} undertone`;
      step.querySelector('.aiw-quiz__match-swatch').style.background = shade.color;

      const concerns = Array.isArray(this.answers.concerns) ? this.answers.concerns : [];
      step.querySelector('[data-row="skin"]').textContent = this.answers.skin || '—';
      step.querySelector('[data-row="concerns"]').textContent = concerns.length ? concerns.join(', ') : '—';
      step.querySelector('[data-row="coverage"]').textContent = this.answers.coverage || 'Full face or spot coverage';
    }

    if (type === 'disc') {
      step.querySelector('.aiw-quiz__disc-shade').textContent = `${shade.name} · ${undertone.short} undertone`;
    }
  }
}

customElements.define('aiw-quiz', AiwQuiz);
